import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { EmailOtpType } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { z } from "zod";

const welcomeSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters").max(100),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const OTP_TYPES: EmailOtpType[] = ["magiclink", "email", "recovery", "invite", "signup"];

// Supabase redirects here with "#error_code=otp_expired&..." when an old-style
// link was already used or expired.
function hashError(): string | null {
  const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return h.get("error_code") || h.get("error");
}

/**
 * Shown when the link is expired/invalid: lets the person email themselves a
 * fresh link (request-invite-link only sends to invited, unclaimed accounts).
 */
function RequestNewLink() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("request-invite-link", {
        body: { email: email.trim() },
      });
      if (error) throw error;
      if (data && data.success === false) throw new Error(data.error);
      setSent(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error && err.message ? err.message : "Could not send a new link. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">This link has expired</CardTitle>
        <CardDescription>
          Sign-in links only work for a limited time and can be used once. Enter the email address
          the invitation was sent to and we&apos;ll email you a new one.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {sent ? (
          <p className="text-sm text-muted-foreground" data-testid="welcome-resend-sent">
            If that address belongs to an invited account that hasn&apos;t been set up yet, a new link
            is on its way. Please check your inbox (and spam folder) and use the newest email.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="resend_email">Email address</Label>
              <Input
                id="resend_email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={sending || !email.includes("@")}>
              {sending ? "Sending..." : "Email me a new link"}
            </Button>
          </form>
        )}
        <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
          Back to login
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Welcome() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenHash = searchParams.get("token_hash");
  const rawType = searchParams.get("type") as EmailOtpType | null;
  const otpType: EmailOtpType = rawType && OTP_TYPES.includes(rawType) ? rawType : "magiclink";
  const [verifying, setVerifying] = useState(false);
  const [linkFailed, setLinkFailed] = useState<boolean>(() => !!hashError());

  // The token is only consumed here, on an explicit click — never on page
  // load — so mail scanners that pre-open the link can't use it up.
  const handleContinue = async () => {
    if (!tokenHash) return;
    setVerifying(true);
    try {
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: otpType });
      if (error) throw error;
      setSearchParams({}, { replace: true });
    } catch (err) {
      console.warn("Invite link verification failed:", err);
      setSearchParams({}, { replace: true });
      setLinkFailed(true);
    } finally {
      setVerifying(false);
    }
  };
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [isEighteenPlus, setIsEighteenPlus] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!isEighteenPlus) return;

    setErrors({});
    setSubmitting(true);

    try {
      const validated = welcomeSchema.parse({ password, confirmPassword });

      const { error: pwError } = await supabase.auth.updateUser({
        password: validated.password,
      });
      if (pwError) throw pwError;

      const { error: profError } = await supabase
        .from("profiles")
        .update({
          status: "active",
          owned_by: user.id,
          claimed_at: new Date().toISOString(),
        })
        .eq("id", user.id);
      if (profError) throw profError;

      toast.success("Welcome! Your account is ready.");
      navigate("/dashboard");
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast.error(error?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
        {children}
      </div>
    </div>
  );

  // Link from an invite email: wait for an explicit click before using it.
  if (tokenHash && !linkFailed) {
    return shell(
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Welcome to MAS Matrimonial</CardTitle>
          <CardDescription>
            Click below to confirm it&apos;s you, then choose a password for your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={handleContinue} disabled={verifying} data-testid="welcome-continue">
            {verifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking your link...
              </>
            ) : (
              "Continue to set your password"
            )}
          </Button>
        </CardContent>
      </Card>,
    );
  }

  // Auth still hydrating
  if (!user && authLoading && !linkFailed) {
    return shell(<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />);
  }

  // No session (or the link was rejected) -> expired/invalid; offer a new link.
  if (!user || linkFailed) {
    return shell(<RequestNewLink />);
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Welcome to MAS Matrimonial</CardTitle>
            <CardDescription>
              Set a password to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="flex items-start space-x-2 rounded-md border border-border bg-muted/30 p-3">
                <Checkbox
                  id="welcome_is_eighteen_plus"
                  checked={isEighteenPlus}
                  onCheckedChange={(checked) => setIsEighteenPlus(checked === true)}
                />
                <Label
                  htmlFor="welcome_is_eighteen_plus"
                  className="font-normal cursor-pointer leading-snug"
                >
                  I confirm I am 18 years of age or older.
                </Label>
              </div>

              <Button type="submit" className="w-full" disabled={submitting || !isEighteenPlus}>
                {submitting ? "Setting password..." : "Set password and continue"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
